/**
 * A password field with a reveal button at its inline end (§7.21, P29): a
 * 32px eye inside the field, which keeps 44px free for it. The field is
 * always left to right, as passwords are typed.
 */
import { useState, type InputHTMLAttributes, type Ref } from 'react';
import { useT } from '../i18n';
import { Icon } from '../ui/kit';

export function PasswordInput({
  className,
  ref,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { ref?: Ref<HTMLInputElement> }) {
  const { t } = useT();
  const [show, setShow] = useState(false);
  const label = t(show ? 'auth.pw.hide' : 'auth.pw.show');
  return (
    <span className="ns-pw">
      <input
        {...rest}
        ref={ref}
        type={show ? 'text' : 'password'}
        dir="ltr"
        spellCheck={false}
        autoCapitalize="none"
        className={['nx-input', className].filter(Boolean).join(' ')}
        // room for the eye (§7.21: padding-inline-end 44); the field's look is the kit's
        style={{ paddingInlineEnd: 44, ...rest.style }}
      />
      <button
        type="button"
        className="ns-pw-eye"
        aria-label={label}
        aria-pressed={show}
        data-tip={label}
        // the field keeps the caret: a click on the eye does not take the focus away
        onPointerDown={(e) => e.preventDefault()}
        onClick={() => setShow((v) => !v)}
      >
        <Icon name={show ? 'cm-eye-off' : 'eye'} size="s" />
      </button>
    </span>
  );
}
